import React, { useRef } from "react";

import { faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon, Tooltip } from "@performyard/py-components";

import { AskPyConversationMenu } from "./AskPyConversationMenu";

import { useAskConversationHistoryEnabled } from "../../hooks/useAskConversationHistoryEnabled";

import { getAskConversation } from "../../../../api/services/defs/internal-api/ai";

import { useAskPerformYardStore } from "../../AskPerformYardStore";

export const AskPyPanelHeader: React.FC = () => {
	const resetChat = useAskPerformYardStore((state) => state.resetChat);
	const closePanel = useAskPerformYardStore((state) => state.closePanel);
	const conversationTitle = useAskPerformYardStore((state) => state.conversationTitle);
	const conversationId = useAskPerformYardStore((state) => state.conversationId);
	const loadConversation = useAskPerformYardStore((state) => state.loadConversation);
	const setIsLoadingConversation = useAskPerformYardStore((state) => state.setIsLoadingConversation);
	const setError = useAskPerformYardStore((state) => state.setError);

	const historyEnabled = useAskConversationHistoryEnabled();

	/** The conversation most recently asked for, so superseded fetches can be dropped. */
	const latestSelectionRef = useRef<string | null>(null);

	// Falls back to the product name until the first turn names the conversation.
	const title = conversationTitle ?? "Ask PerformYard";

	// Fetched imperatively rather than through a query hook keyed on a selected
	// id: the thread only needs to enter the store once, when the user picks it.
	// A hook would re-render the panel on every cache touch and re-seed a
	// transcript the user has since added to.
	const handleSelectConversation = async (selectedId: string): Promise<void> => {
		// `loadConversation` replaces the thread, the title, the card states and the
		// stored pointer, so a slow first fetch resolving after a second one would
		// durably settle the panel on the conversation the user did not pick last.
		// Same guard as useRestoreActiveConversation, which re-checks after its await.
		latestSelectionRef.current = selectedId;

		setIsLoadingConversation(true);
		try {
			const conversation = await getAskConversation(selectedId);
			if (latestSelectionRef.current !== selectedId) return;
			loadConversation(conversation);
		} catch {
			if (latestSelectionRef.current !== selectedId) return;
			setIsLoadingConversation(false);
			setError("We couldn't open that conversation. Please try again.");
		}
	};

	return (
		<div className="box-border flex h-12 w-full shrink-0 items-center justify-between gap-2 border-b border-solid border-neutral-2 bg-white px-3">
			<div data-testid="ask-py-panel-title" className="flex min-w-0 items-center gap-2">
				{historyEnabled ? (
					<AskPyConversationMenu
						title={title}
						activeConversationId={conversationId}
						onSelect={(selectedId) => void handleSelectConversation(selectedId)}
					/>
				) : (
					<div className="flex min-w-0 items-center gap-2 px-1">
						<img src="/static/img/py-logo/py-round-icon.svg" alt="" className="size-6 shrink-0" />
						<span className="truncate text-base leading-6 text-neutral-9">{title}</span>
					</div>
				)}
			</div>
			{/* standard/16 is the design's ButtonIcon/Standard treatment but leaves a bare 16×16 target;
			    size-8 re-widens the box to 32×32 (WCAG 2.5.8) while getIconSize keeps the glyph at 16px. */}
			<div className="flex shrink-0 items-center gap-1">
				<Tooltip content="New Chat" placement="bottom" size="small" asChild>
					<ButtonIcon
						icon={faPenToSquare}
						onClick={resetChat}
						variant="standard"
						color="neutral"
						shade={5}
						size={16}
						dangerouslySetClassName="size-8"
						aria-label="New chat"
					/>
				</Tooltip>
				<Tooltip content="Close" placement="bottom" size="small" asChild>
					<ButtonIcon
						icon={faXmark}
						onClick={closePanel}
						variant="standard"
						color="neutral"
						shade={5}
						size={16}
						dangerouslySetClassName="size-8"
						aria-label="Close Ask PerformYard"
					/>
				</Tooltip>
			</div>
		</div>
	);
};

export default AskPyPanelHeader;
